import { Popover } from "@xola/ui-kit";
import clsx from "clsx";
import React, { Children, cloneElement, ComponentProps, isValidElement, ReactElement, ReactNode } from "react";

interface SidebarTabsProps {
    activeTab: number;
    onClick: (index: number) => void;
    children: ReactNode;
}

export const SidebarTabs = ({ activeTab = 0, onClick, children }: SidebarTabsProps) => {
    const currentTab = Children.toArray(children)[activeTab];
    const content = isValidElement(currentTab) ? currentTab.props.children : null;

    return (
        <div className="flex h-full">
            <div className="w-24 flex-shrink-0 overflow-y-auto border-r border-gray-light lg:w-64 xl:w-75">
                {Children.map(children, (tab, index) => {
                    if (!tab) {
                        return null;
                    }

                    return cloneElement(tab as ReactElement, {
                        isActive: index === activeTab,
                        onClick: () => onClick(index),
                    });
                })}
            </div>

            <div className={clsx("flex-1 overflow-y-auto")}>{content}</div>
        </div>
    );
};

interface TabProps extends ComponentProps<"div"> {
    name: string;
    description: string;
    icon: ReactElement;
    isActive?: boolean;
    children: ReactNode;
    className?: string;
    isHidden?: boolean;
}

const Tab = ({ name, description, icon, isActive = false, isHidden = false, className, ...rest }: TabProps) => {
    return isHidden ? null : (
        <div
            role="button"
            className={clsx(
                "flex cursor-pointer items-center border-b border-gray-light p-8 tracking-tightest",
                isActive && "bg-primary-lighter",
                className,
            )}
            {...rest}
        >
            <div className="hidden space-x-2 lg:flex">
                <span>{cloneElement(icon, { size: "medium", className: "mt-0.5" })}</span>
                <TabDescription name={name} description={description} />
            </div>

            <div className="space-x-2 lg:hidden">
                <Popover placement="right" delay={300} maxWidth={300}>
                    <Popover.Content>
                        <TabDescription name={name} description={description} className="p-2" />
                    </Popover.Content>
                    <span>{cloneElement(icon, { size: "large", className: "mt-0.5" })}</span>
                </Popover>
            </div>
        </div>
    );
};

interface TabDescriptionProps {
    name: string;
    description?: string;
    className?: string;
}

const TabDescription = ({ name, description, className }: TabDescriptionProps) => {
    return (
        <div className={className}>
            <p className="font-bold">{name}</p>
            <p className="leading-p1 text-gray-darker">{description}</p>
        </div>
    );
};

SidebarTabs.Tab = Tab;
